/**
 * VariantCredentialsPanel — per-variant credential management.
 *
 * Restored after the #1258 Test Data redesign re-routed `/test-data` to the
 * workspace-scoped `TestDataSetsCatalog` and inadvertently dropped this
 * variant-scoped surface. The redesign was scoped to Shared Data Sets →
 * TestDataSet and explicitly intended credentials to remain ("use the
 * existing credentials system for real secrets, not Test Data sets"); the
 * old `/test-data` page just happened to co-host both, so the cutover
 * evicted credentials as collateral. This restores it as the "Credentials"
 * tab of the new `/test-data` surface.
 *
 * #1312 (Slice 2 of #1311) refreshed the editor card into four labelled
 * sections — Who this is · How they sign in · Login details · Notes — and
 * added the sign-in experience the deterministic resolver (#1287/#1315)
 * always needed an authoring surface for:
 *   - a per-credential "Sign-in address" the operator can type (writes the
 *     first step's `loginUrl`, which the run-start resolver ranks first);
 *   - an auto-detected sign-in helper shown inline with a "Change" opt-out
 *     (the detected method comes from the backend, computed with the SAME
 *     matcher a run uses, so the preview can't disagree with the run);
 *   - a soft heads-up when no way to sign in is set up for a role.
 * The single-step case is flattened — the login-detail fields render inline
 * with no nested "Step 1" card; the step framing appears only after the
 * operator adds a second step for a multi-page sign-in.
 *
 * NOTE: this duplicates ~700 lines that still live in the now-orphaned,
 * unrouted `pages/TestData.tsx`. That file is slated for deletion by the
 * redesign owners; until then it is never edited (no route reaches it), so
 * the duplication cannot drift. When it is deleted this becomes the sole
 * home. See the fix PR's regression analysis.
 */
import { useState, useEffect } from 'react'
import { Plus, Trash2, Copy, ChevronDown, ChevronRight, Info } from 'lucide-react'
import { Button, Input, PasswordInput, Label, Tooltip } from '@/components/ui'
import { testDataAPI, systemFixturesAPI, type SystemFixtureSummary, type DetectedSignInMethod } from '@/lib/api'
import type { LoginFixtureBinding } from '@/types'
import { useAppContext } from '@/hooks/useAppContext'
import { useConfirm } from '@/hooks/useConfirm'
import { useToast } from '@/hooks/useToast'

// ── Types (lifted from the old TestData page) ─────────────────────────
type AuthMethod = 'basic' | 'bearer' | 'mfa' | 'oauth' | 'certificate'

interface CredentialStep {
  name: string
  method: AuthMethod
  username?: string
  password?: string
  token?: string
  totpSecret?: string
  clientId?: string
  clientSecret?: string
  tokenEndpoint?: string
  /** The sign-in address for this step. The operator can type it in the
   * "Sign-in address" field (#1312); the agent can also capture it via
   * `fill_login_step` during exploration. Sourced into
   * AUTH_<ROLE>_LOGIN_URL at run time and ranked first by the run-start
   * resolver. */
  loginUrl?: string
  /** Provenance for `loginUrl` (#1349): 'operator' = the tester's choice and
   * authoritative; 'discovered' = auto-found and safe to re-discover. */
  loginUrlSource?: 'operator' | 'discovered'
  /** A sign-in address OttoTester found that differs from the saved `loginUrl`
   * (#1914). Read-only; the credential screen offers it as a suggestion the
   * tester can accept (#1915). `At` is the ISO time it was last seen. */
  suggestedLoginUrl?: string
  suggestedLoginUrlAt?: string
  notes?: string
}

interface Credential {
  id: string
  role?: string
  label?: string
  steps: CredentialStep[]
  /** Per-credential binding to a SystemFixture library entry. */
  loginFixtureBinding?: LoginFixtureBinding
  /** Read-only sign-in helper the backend matched from this credential's
   * known sign-in addresses (#1312). Null when nothing matched. Refreshed
   * on each load; never written back. */
  detectedSignInMethod?: DetectedSignInMethod | null
  /** Read-only id of the sign-in helper that will ACTUALLY run for this
   * credential (#1884 follow-up): the explicitly chosen one, or the one
   * already installed for this role while the credential is on `auto`. Its
   * required login details are marked required here — an `auto` credential
   * used to show them as optional and then fail the run on a missing
   * sign-in address. Null when no helper applies; never written back. */
  effectiveFixtureId?: string | null
  /** Read-only: true when this role has a recorded sign-in path OttoTester
   * can replay (#1913). A recording reaches the form on its own, so it counts
   * as a way to sign in and the sign-in address stops being required. Refreshed
   * on each load; never written back. */
  hasUsableSignInRecording?: boolean
}

const METHOD_OPTIONS = [
  { value: 'basic', label: 'Basic (user/pass)' },
  { value: 'bearer', label: 'Bearer / API Key' },
  { value: 'mfa', label: 'MFA (user/pass + authenticator)' },
  { value: 'oauth', label: 'OAuth Client Credentials' },
  { value: 'certificate', label: 'Certificate' },
]

/** Display form of a discovered sign-in address — origin + path only.
 * IdP/OIDC authorize URLs carry per-run `state`/`nonce` query params that
 * are noisy and best not shown (planner-sourced URLs are already
 * normalized backend-side, but scanner-observed ones may still carry a
 * query). The full URL stays available in the row's `title` tooltip.
 * Falls back to the raw string if it doesn't parse. */
export function displaySignInUrl(url: string): string {
  try {
    const u = new URL(url)
    return `${u.origin}${u.pathname}`
  } catch {
    return url
  }
}

/** Light validation for the operator-typed sign-in address. Empty is
 * allowed (the field is optional); a non-empty value must parse as an
 * http(s) URL. Deliberately permissive — we don't block a save on a value
 * the run might still resolve; the hint is advisory. Exported so the rule
 * is locked in a focused test. */
export function isLikelyHttpUrl(value: string): boolean {
  const v = value.trim()
  if (!v) return true
  try {
    const u = new URL(v)
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

/** True when a catalog `urlPatterns` entry is usable for host recognition
 * (#1896). Entries are contractually PLAIN HOST STRINGS — see the
 * `SystemFixture` schema — so anything carrying a scheme, a path, or
 * whitespace is malformed for this purpose and is ignored rather than
 * guessed at. Keeps a future bad catalog row from silently changing which
 * addresses get warned about. */
function isPlainHostPattern(pattern: string): boolean {
  return pattern.length > 0 && !/[\s/]/.test(pattern) && !pattern.includes(':')
}

/** Normalize a catalog pattern to the bare host it means (#1896).
 *
 * The seeded catalog stores plain hosts (`okta.com`), but a leading `*.`
 * shows up in hand-written entries. Both mean the same thing here — "this
 * provider's domain" — so a wildcard prefix is stripped rather than left to
 * silently match nothing. Stripping also makes the apex itself match, which
 * is what we want: `okta.com` IS the provider's site. */
function bareHostPattern(pattern: string): string {
  return pattern.startsWith('*.') ? pattern.slice(2) : pattern
}

/**
 * The sign-in provider whose own website this address belongs to, or null
 * (#1896).
 *
 * The sign-in helper navigates to this address and expects to end up back
 * on the app. That only happens when it is the app's OWN sign-in page,
 * which hands off to the provider and gets redirected back. Pointed at the
 * provider's website instead, sign-in succeeds and simply stays there —
 * no app session, ever. This spots that mistake so the field can say so.
 *
 * Matching mirrors the server's `hostMatchesPattern` — hostname EQUALS the
 * pattern, or ends with `.` + pattern — run against the parsed hostname,
 * never a substring of the raw address. That boundary is what stops
 * `login.gov.example.com` (an app that merely looks like the provider)
 * from being flagged. The server's matcher lives in the application layer
 * and can't be imported here, so this is a deliberate, narrow restatement
 * of that one rule; the pattern LIST is not duplicated — it comes from the
 * fetched catalog, so a newly seeded provider is covered for free.
 *
 * Returns the provider's display name because the warning names it.
 * Advisory only: an unparseable or relative address returns null
 * (`isLikelyHttpUrl` already covers "that isn't a web address").
 */
export function findSignInProviderForAddress(
  value: string,
  systemFixtures: SystemFixtureSummary[],
): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  let hostname: string
  try {
    hostname = new URL(trimmed).hostname.toLowerCase()
  } catch {
    return null
  }
  if (!hostname) return null
  for (const fixture of systemFixtures) {
    for (const raw of fixture.urlPatterns ?? []) {
      const trimmedPattern = raw.trim().toLowerCase()
      if (!isPlainHostPattern(trimmedPattern)) continue
      const pattern = bareHostPattern(trimmedPattern)
      if (!pattern) continue
      if (hostname === pattern || hostname.endsWith('.' + pattern)) return fixture.name
    }
  }
  return null
}

/** Plain-language summary of how this credential will sign in, from the
 * operator's choice or the auto-detected match. Drives the read-only line
 * in the "How they sign in" section (#1312):
 *   - a pinned helper or an explicit "my own login form" choice is stated
 *     as a settled fact ("Using …");
 *   - an auto-detected match is COMMITTED on a strong match ("Using …",
 *     detected automatically) and merely SUGGESTED on a weak one
 *     ("Looks like …") — commit-on-high, propose-on-low.
 * Exported so the by-confidence presentation is locked in a test without
 * rendering the whole editor. */
export function describeSignInMethod(
  binding: LoginFixtureBinding | undefined,
  detected: DetectedSignInMethod | null | undefined,
  systemFixtures: SystemFixtureSummary[],
): { text: string; hint?: string; tone: 'active' | 'suggested' | 'custom' } {
  if (binding?.mode === 'library' && binding.libraryId) {
    const f = systemFixtures.find(s => s.id === binding.libraryId)
    return { text: `Using ${f?.name ?? 'a built-in sign-in helper'}`, tone: 'active' }
  }
  if (binding?.mode === 'none') {
    return { text: 'Using your own login form', tone: 'custom' }
  }
  // Auto / not yet chosen — present the detected match by confidence.
  if (detected) {
    return detected.confidence === 'high'
      ? { text: `Using ${detected.name}`, hint: 'Detected automatically.', tone: 'active' }
      : { text: `Looks like ${detected.name}`, hint: 'Detected automatically — worth a check before your run.', tone: 'suggested' }
  }
  return { text: 'OttoTester will work out how to sign in when it explores the app', tone: 'suggested' }
}

/** True when OttoTester appears to have SOME way to sign in as this role.
 * The signals from #1312: a sign-in address the operator typed, a pinned
 * helper, or a helper detected from a discovered address. Plus two guards: an explicit "my own
 * login form" choice is itself a plan, and a first step already carrying
 * login details is a way to sign in. When this returns false the card
 * shows a soft heads-up and the header shows the "No sign-in set up yet"
 * badge. Soft by design — auto-detect resolves at run time, so a false
 * here is a heuristic nudge, not a hard error. Exported for the warning
 * test. */
export function hasSignInSignal(
  cred: { loginFixtureBinding?: LoginFixtureBinding; steps?: CredentialStep[]; hasUsableSignInRecording?: boolean },
  detected: DetectedSignInMethod | null | undefined,
): boolean {
  const b = cred.loginFixtureBinding
  if (b?.mode === 'library' && b.libraryId) return true    // a pinned helper
  if (b?.mode === 'none') return true                       // chose their own login form
  if (cred.hasUsableSignInRecording) return true            // a recorded sign-in path to replay (#1913)
  const step0 = cred.steps?.[0]
  if (step0?.loginUrl && step0.loginUrl.trim()) return true // a typed sign-in address
  if (detected) return true                                 // a detected helper (matched from a discovered address)
  // The operator's own filled-in login details are a way to sign in.
  if (step0 && (step0.username?.trim() || step0.password?.trim() || step0.token?.trim())) return true
  return false
}

export function CredentialsEditor({ credentials, onChange, variantId, variantName, variantUrl }: {
  credentials: Credential[]
  onChange: (c: Credential[]) => void
  variantId?: string
  variantName?: string
  variantUrl?: string
}) {
  // #1148: shared themed confirm (was a prop threaded from the page).
  const confirm = useConfirm()
  // #1149: shared themed toast, replacing native window.alert().
  const toast = useToast()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // #1915 — "Keep mine" hides a found-address suggestion for the session,
  // keyed by (credential id, found address) so a DIFFERENT found address for
  // the same credential shows again. Client-only; reset on variant change so a
  // dismissal never leaks across variants (Copilot review).
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set())
  useEffect(() => { setDismissedSuggestions(new Set()) }, [variantId])
  const [systemFixtures, setSystemFixtures] = useState<SystemFixtureSummary[]>([])

  // SystemFixture library catalog drives the per-credential sign-in
  // picker (#876 Slice 1). Cross-workspace and ~constant, so a single
  // fetch on mount is enough. #886: filter out internal diagnostic
  // entries (e.g. noopSmoke) — the catalog API returns them for scripts
  // and tests but the customer-facing picker should never surface them.
  useEffect(() => {
    if (!variantId) { setSystemFixtures([]); return }
    systemFixturesAPI.list()
      .then(all => setSystemFixtures(all.filter(f => !INTERNAL_FIXTURE_IDS.has(f.id))))
      .catch(() => setSystemFixtures([]))
  }, [variantId])
  // Edits to an expanded credential stage in `drafts` — field changes
  // update the draft only. Save commits the draft to `onChange`. Cancel
  // discards. Collapsing the card auto-saves any pending draft so users
  // don't lose work by clicking the chevron.
  const [drafts, setDrafts] = useState<Record<string, Credential>>({})

  function view(cred: Credential): Credential {
    return drafts[cred.id] ?? cred
  }

  function editDraft(id: string, updater: (c: Credential) => Credential) {
    setDrafts(d => {
      const base = d[id] ?? credentials.find(c => c.id === id)
      if (!base) return d
      return { ...d, [id]: updater(base) }
    })
  }

  function clearDraft(id: string) {
    setDrafts(d => { const next = { ...d }; delete next[id]; return next })
  }

  function commitDraft(id: string): boolean {
    const draft = drafts[id]
    if (!draft) return true // nothing to commit
    const trimmed = (draft.role || '').trim()
    if (trimmed && credentials.some(c => c.id !== id && (c.role || '').trim() === trimmed)) {
      toast({
        title: `Role "${trimmed}" is already used by another credential`,
        description: 'Roles must be unique within a variant.',
        variant: 'error',
      })
      return false
    }
    // Block the save when the chosen sign-in helper still needs a login
    // detail (the Save button is disabled in the same case; this also
    // guards the collapse-auto-save path so the block can't be dodged by
    // clicking the chevron). The server enforces the same rule as a
    // backstop for API callers. #1884
    const step0 = draft.steps?.[0]
    const missing = step0
      ? findMissingRequiredFields(draft.loginFixtureBinding, systemFixtures, step0, draft.effectiveFixtureId, draft.hasUsableSignInRecording)
      : []
    if (missing.length > 0) {
      const boundFixture = systemFixtures.find(
        f => f.id === (draft.effectiveFixtureId ?? draft.loginFixtureBinding?.libraryId),
      )
      toast({
        title: `Add the ${joinForProse(missing.map(f => FIELD_LABEL[f]))} before saving`,
        description: `${boundFixture?.name ?? 'This sign-in helper'} can't sign in without ${missing.length === 1 ? 'it' : 'them'}.`,
        variant: 'error',
      })
      return false
    }
    const renumbered = renumberSteps(draft)
    onChange(credentials.map(c => c.id === id ? renumbered : c))
    clearDraft(id)
    return true
  }

  // #1915 — one-click accept of the address OttoTester found. Writes it into
  // the sign-in box as the tester's choice (`loginUrlSource: 'operator'`) and
  // clears the suggestion, then saves through the same onChange the editor
  // already uses. Adding an address only ADDS a sign-in signal, so this needs
  // none of commitDraft's required-field guards.
  function acceptFoundAddress(cred: Credential) {
    const v = view(cred)
    if (!v.steps?.[0]?.suggestedLoginUrl?.trim()) return
    const accepted = applyFoundAddress(v)
    onChange(credentials.map(c => c.id === cred.id ? accepted : c))
    clearDraft(cred.id)
  }

  function dismissSuggestion(id: string, url: string) {
    setDismissedSuggestions(prev => new Set(prev).add(`${id}::${url}`))
  }

  function addCredential() {
    const id = crypto.randomUUID()
    const fresh: Credential = { id, role: '', label: '', steps: [newStep(1)] }
    onChange([...credentials, fresh])
    setExpanded(prev => new Set(prev).add(id))
  }

  function duplicateCredential(i: number) {
    const src = credentials[i]
    const copyRole = makeUniqueRole((src.role || '') + '-copy', credentials)
    const dupe: Credential = {
      id: crypto.randomUUID(),
      role: copyRole,
      label: src.label,
      steps: src.steps.map(s => ({ ...s })),
    }
    const next = [...credentials.slice(0, i + 1), dupe, ...credentials.slice(i + 1)]
    onChange(next)
    setExpanded(prev => new Set(prev).add(dupe.id))
  }

  async function deleteCredential(i: number) {
    const c = credentials[i]
    const descriptor = c.role || c.label || c.steps[0]?.username || '(unnamed)'
    const ok = await confirm({
      title: `Delete credential "${descriptor}"?`,
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!ok) return
    clearDraft(c.id)
    onChange(credentials.filter((_, idx) => idx !== i))
  }

  function toggleExpanded(id: string) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        // Collapsing — auto-save any pending draft so nothing is lost.
        if (drafts[id] && !commitDraft(id)) return prev  // keep open on save error
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Header heads-up: true when ANY role has no way to sign in set up yet.
  const anyMissingSignIn = credentials.some(c => {
    const v = view(c)
    return !hasSignInSignal(v, v.detectedSignInMethod)
  })

  return (
    <div className="p-5">
      <div className="mb-4">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2 flex-wrap">
          <span>
            Credentials
            {variantName && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">· {variantName}</span>
            )}
          </span>
          {anyMissingSignIn && (
            <span
              className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600"
              title="One or more roles don't have a way to sign in set up yet. OttoTester may still work it out while it explores, but it's worth a look."
            >
              No sign-in set up yet
            </span>
          )}
        </h2>
        {variantUrl && (
          <p className="text-[11px] font-mono text-muted-foreground mb-2 truncate">{variantUrl}</p>
        )}
        <p className="text-xs text-muted-foreground">
          Apply only to this variant — never shared with other variants of this application. Inherited by
          every test case in this variant. Each credential has one or more login steps — add a second step
          for a multi-page sign-in (e.g. a federated sign-in followed by the app's own login).
        </p>
      </div>

      <div className="space-y-2">
        {credentials.map((cred, i) => {
          const isOpen = expanded.has(cred.id)
          const v = view(cred)
          const dirty = !!drafts[cred.id]
          const step0 = v.steps[0]
          // Missing required-field warning applies only to the first step
          // (the canonical credential row the env composer reads from).
          const missing = step0
            ? findMissingRequiredFields(v.loginFixtureBinding, systemFixtures, step0, v.effectiveFixtureId, v.hasUsableSignInRecording)
            : []
          // The helper that will actually run — an explicitly chosen one, or
          // one already installed for this role (#1884 follow-up). Drives the
          // required/optional hints below, so they match what the run demands.
          const boundFixture = systemFixtures.find(
            f => f.id === (v.effectiveFixtureId ?? (v.loginFixtureBinding?.mode === 'library' ? v.loginFixtureBinding.libraryId : undefined)),
          )
          const signedInOk = hasSignInSignal(v, v.detectedSignInMethod)
          // #1896 — null unless the saved address is a sign-in provider's own
          // site. Derived from the current value on every render, so it covers
          // both live typing and a pre-existing bad value.
          const providerForAddress = findSignInProviderForAddress(step0?.loginUrl || '', systemFixtures)
          return (
            <div key={cred.id} className="border rounded-lg overflow-hidden bg-background">
              <div className="flex items-center gap-2 px-3 py-2 hover:bg-muted/20">
                <button
                  className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  onClick={() => toggleExpanded(cred.id)}
                  aria-label={isOpen ? 'collapse' : 'expand'}
                >
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
                <div className="flex-1 flex items-baseline gap-3 min-w-0">
                  <span className="font-medium text-sm truncate">{v.role || <span className="text-muted-foreground italic">no role</span>}</span>
                  <span className="text-xs text-muted-foreground truncate">{summarizeSteps(v.steps)}</span>
                  {v.label && <span className="text-xs text-muted-foreground truncate italic">· {v.label}</span>}
                  {dirty && <span className="text-[10px] text-amber-500">· unsaved</span>}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon-sm" title="Duplicate" onClick={() => duplicateCredential(i)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon-sm" title="Delete" onClick={() => { void deleteCredential(i) }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t bg-muted/10">
                  {/* ── Who this is ─────────────────────────────────── */}
                  <section className="px-3 pt-3 pb-3 space-y-2">
                    <SectionHeader>Who this is</SectionHeader>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Role</Label>
                        <Input
                          className="h-8 text-xs"
                          value={v.role || ''}
                          placeholder="e.g. admin, editor, reviewer"
                          onChange={e => editDraft(cred.id, c => ({ ...c, role: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Display name (optional)</Label>
                        <Input
                          className="h-8 text-xs"
                          value={v.label || ''}
                          placeholder="A friendly name shown on screen"
                          onChange={e => editDraft(cred.id, c => ({ ...c, label: e.target.value }))}
                        />
                      </div>
                    </div>
                  </section>

                  {/* ── How they sign in ────────────────────────────── */}
                  <section className="px-3 py-3 space-y-2 border-t border-border/40">
                    <SectionHeader>How they sign in</SectionHeader>
                    <SignInMethodSection
                      credentialId={cred.id}
                      step1Method={step0?.method ?? 'basic'}
                      binding={v.loginFixtureBinding}
                      detected={v.detectedSignInMethod}
                      systemFixtures={systemFixtures}
                      onChange={selection => editDraft(cred.id, c => applyAuthSelection(c, selection))}
                    />

                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1.5">
                        {/* The bound sign-in helper decides whether the
                            address is required — flip the hint so the
                            operator sees it's needed before they hit a
                            blocked Save (#1884). A recorded sign-in path makes
                            the address optional too (#1913). */}
                        Sign-in address {boundFixture?.requiredFields.includes('LOGIN_URL') && !boundFixture?.authEntryPathHints?.length && !v.hasUsableSignInRecording ? '(required)' : '(optional)'}
                        {/* #1896 — the round trip is the whole point of this
                            field, and the field used to teach the opposite
                            (its placeholder was a provider address). */}
                        <Tooltip
                          content="Enter the sign-in page on your own app. It hands off to your sign-in provider and brings you back afterwards. Don't enter the provider's own website — signing in there never returns to your app."
                          triggerLabel="Which sign-in address should I enter?"
                        >
                          <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                        </Tooltip>
                      </Label>
                      <Input
                        className="h-8 text-xs"
                        value={step0?.loginUrl || ''}
                        placeholder="https://app.example.com/sign-in"
                        onChange={e => editDraft(cred.id, c => setFirstStepLoginUrl(c, e.target.value))}
                      />
                      {step0?.loginUrl && !isLikelyHttpUrl(step0.loginUrl) && (
                        <p className="text-[11px] text-amber-500">
                          That doesn't look like a web address — it should start with http:// or https://.
                        </p>
                      )}
                      {/* Computed during render (not in the change handler) so a
                          credential that ALREADY holds a provider address warns
                          the moment the editor opens — which is how the saved
                          value that motivated #1896 went unnoticed. */}
                      {providerForAddress && (
                        <p className="text-[11px] text-amber-500">
                          This looks like {providerForAddress}'s own website. Enter the sign-in page on your app
                          instead — it hands off to {providerForAddress} and returns after signing in.
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground">
                        The page on your own app where signing in starts — not the provider's own site. If you set it,
                        OttoTester uses it ahead of anything it found on its own.
                      </p>
                      {/* #1915 — the address OttoTester found by following your
                          app's sign-in, shown only when it differs from the saved
                          value and the tester hasn't dismissed it for now. */}
                      {(() => {
                        const found = step0?.suggestedLoginUrl?.trim()
                        const dismissed = !!found && dismissedSuggestions.has(`${cred.id}::${found}`)
                        if (!found || !shouldShowFoundSuggestion(step0, dismissed)) return null
                        return (
                          <div className="mt-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2.5 space-y-1.5">
                            <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                              OttoTester found where sign-in actually starts
                            </p>
                            <p className="font-mono text-[11px] break-all">{displaySignInUrl(found!)}</p>
                            <p className="text-[11px] text-muted-foreground">
                              Your saved address is different. This is the page OttoTester reached by following your app's sign-in.
                            </p>
                            <div className="flex items-center gap-3 pt-0.5">
                              <Button size="sm" className="h-6 text-[11px] px-2.5" onClick={() => acceptFoundAddress(cred)}>
                                Use the found address
                              </Button>
                              <button
                                type="button"
                                className="text-[11px] text-muted-foreground underline hover:text-foreground"
                                onClick={() => dismissSuggestion(cred.id, found)}
                              >
                                Keep mine
                              </button>
                            </div>
                          </div>
                        )
                      })()}
                    </div>

                    {!signedInOk && (
                      <p className="text-[11px] text-amber-600 flex items-baseline gap-1">
                        <span aria-hidden="true">⚠</span>
                        <span>
                          We don't see a way to sign in as {v.role?.trim() || 'this role'} yet. Add a sign-in
                          address, fill in the login details below, or pick a sign-in helper above.
                        </span>
                      </p>
                    )}
                  </section>

                  {/* ── Login details ───────────────────────────────── */}
                  <section className="px-3 py-3 space-y-2 border-t border-border/40">
                    <SectionHeader>Login details</SectionHeader>
                    {step0 ? (
                      <>
                        {/* Single-step credentials render the login fields
                            inline — no nested "Step 1" card (#1312). The
                            step framing returns only for the 2nd step on. */}
                        <StepFieldGrid
                          step={step0}
                          onChange={updates => editDraft(cred.id, c => ({
                            ...c,
                            steps: c.steps.map((s, idx) => idx === 0 ? { ...s, ...updates } : s),
                          }))}
                        />
                        <MissingFieldsWarning fields={missing} helperName={boundFixture?.name} location="here" />
                      </>
                    ) : null}

                    {v.steps.slice(1).map((step, idx) => {
                      const si = idx + 1
                      return (
                        <StepCard
                          key={si}
                          step={step}
                          stepNumber={si + 1}
                          canRemove={v.steps.length > 1}
                          // Steps 2+ (a multi-page sign-in) keep the per-step
                          // method picker; the first step's method is driven
                          // by the sign-in picker above.
                          showMethodDropdown
                          missingRequiredFields={[]}
                          onChange={updates => editDraft(cred.id, c => ({
                            ...c,
                            steps: c.steps.map((s, j) => j === si ? { ...s, ...updates } : s),
                          }))}
                          onRemove={() => editDraft(cred.id, c => ({
                            ...c,
                            steps: c.steps.filter((_, j) => j !== si),
                          }))}
                        />
                      )
                    })}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => editDraft(cred.id, c => ({ ...c, steps: [...c.steps, newStep(c.steps.length + 1)] }))}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add a step
                    </Button>
                  </section>

                  {/* ── Notes ───────────────────────────────────────── */}
                  <section className="px-3 py-3 space-y-2 border-t border-border/40">
                    <SectionHeader>Notes</SectionHeader>
                    <Input
                      className="h-8 text-xs"
                      value={step0?.notes || ''}
                      placeholder="Anything OttoTester should know about this sign-in"
                      onChange={e => editDraft(cred.id, c => ({
                        ...c,
                        steps: c.steps.length === 0
                          ? [{ ...newStep(1), notes: e.target.value }]
                          : c.steps.map((s, idx) => idx === 0 ? { ...s, notes: e.target.value } : s),
                      }))}
                    />
                  </section>

                  <div className="px-3 pb-3 pt-2 flex items-center justify-end gap-2 border-t border-border/40">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!dirty}
                      onClick={() => clearDraft(cred.id)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      // Blocked while the bound sign-in helper is still
                      // missing a required login detail (#1884) — the inline
                      // warning above names what's needed.
                      disabled={!dirty || missing.length > 0}
                      title={missing.length > 0 ? 'Add the required login details above before saving' : undefined}
                      onClick={() => commitDraft(cred.id)}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <Button variant="outline" size="sm" className="mt-3" onClick={addCredential}>
        <Plus className="h-3 w-3 mr-1" /> Add credential
      </Button>
    </div>
  )
}

/** Small uppercase section label that divides the credential editor into
 * "Who this is / How they sign in / Login details / Notes" (#1312). */
function SectionHeader({ children }: { children: React.ReactNode }) {
  return <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{children}</p>
}

/** Set the first step's sign-in address, creating a step if the
 * credential somehow has none. Kept as a pure helper so the editor's
 * onChange reads cleanly. */
function setFirstStepLoginUrl(c: Credential, url: string): Credential {
  return {
    ...c,
    steps: c.steps.length === 0
      ? [{ ...newStep(1), loginUrl: url }]
      : c.steps.map((s, i) => i === 0 ? { ...s, loginUrl: url } : s),
  }
}

/** #1915 — accept the address OttoTester found: move `suggestedLoginUrl` into
 * `loginUrl` as the tester's own choice (`loginUrlSource: 'operator'`) and
 * clear the suggestion. Pure — the editor's accept handler saves the result
 * through the normal onChange. Returns the credential unchanged when there is
 * no suggestion to accept. Exported for the accept test. */
export function applyFoundAddress(c: Credential): Credential {
  const found = c.steps?.[0]?.suggestedLoginUrl?.trim()
  if (!found) return c
  return {
    ...c,
    steps: c.steps.map((s, i) => i === 0
      ? { ...s, loginUrl: found, loginUrlSource: 'operator', suggestedLoginUrl: undefined, suggestedLoginUrlAt: undefined }
      : s),
  }
}

/** #1915 — whether to show the found-address suggestion under the sign-in box:
 * there is a found address, it differs from the saved one, and the tester
 * hasn't dismissed it for the session. Pure so the show/hide decision is
 * testable without rendering the editor. */
export function shouldShowFoundSuggestion(
  step: { loginUrl?: string; suggestedLoginUrl?: string } | undefined,
  dismissed: boolean,
): boolean {
  const found = step?.suggestedLoginUrl?.trim()
  return !!found && found !== (step?.loginUrl || '').trim() && !dismissed
}

/** Apply a sign-in picker selection to a credential — the three picker
 * groups each translate to a distinct (first-step method, binding) pair.
 * Field values are cleared only when the method actually changes, so
 * re-picking the same custom flow or switching helpers within a method
 * keeps what the operator already typed. Pulled out of the inline
 * onChange (where it lived pre-#1312) so the section component can reuse
 * it. */
function applyAuthSelection(c: Credential, selection: CredentialAuthSelection): Credential {
  const nextMethod = selection.method
  const clearedFields: Partial<CredentialStep> = {
    username: undefined, password: undefined, token: undefined,
    totpSecret: undefined, clientId: undefined,
    clientSecret: undefined, tokenEndpoint: undefined,
  }
  return {
    ...c,
    steps: c.steps.length === 0
      ? [{ ...newStep(1), method: nextMethod }]
      : c.steps.map((s, i) => i === 0
          ? (s.method === nextMethod ? s : { ...s, method: nextMethod, ...clearedFields })
          : s),
    loginFixtureBinding: selection.binding,
  }
}

/** The "How they sign in" read-out + opt-out (#1312). By default it shows
 * a plain-language line stating the chosen or auto-detected sign-in
 * method (commit-on-high, propose-on-low — see `describeSignInMethod`)
 * with a "Change" button. Clicking Change reveals the underlying picker
 * (built-in helper / let OttoTester figure it out / your own login form);
 * "Done" collapses back to the summary. Exported so the by-confidence
 * presentation can be render-tested directly. */
export function SignInMethodSection({ credentialId, step1Method, binding, detected, systemFixtures, onChange }: {
  credentialId: string
  step1Method: AuthMethod
  binding: LoginFixtureBinding | undefined
  detected: DetectedSignInMethod | null | undefined
  systemFixtures: SystemFixtureSummary[]
  onChange: (selection: CredentialAuthSelection) => void
}) {
  const [editing, setEditing] = useState(false)
  const summary = describeSignInMethod(binding, detected, systemFixtures)
  const toneCls = summary.tone === 'suggested' ? 'text-amber-600' : 'text-foreground'

  if (editing) {
    return (
      <div className="space-y-1.5">
        <CredentialAuthPicker
          credentialId={credentialId}
          step1Method={step1Method}
          binding={binding}
          systemFixtures={systemFixtures}
          onChange={onChange}
        />
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Done</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border bg-background px-3 py-2">
      <div className="min-w-0">
        <p className={`text-xs font-medium truncate ${toneCls}`}>{summary.text}</p>
        {summary.hint && <p className="text-[11px] text-muted-foreground">{summary.hint}</p>}
      </div>
      <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setEditing(true)}>Change</Button>
    </div>
  )
}

/** Internal-only fixture ids that should never appear in the customer-
 * facing picker (#886). The catalog API still returns them — they're
 * useful for diagnostic scripts and tests that bind directly — but the
 * picker filters them out client-side. Keep this list tiny; if it
 * grows past 2-3 entries, promote to a `customerFacing` field on the
 * catalog row. Exported so the test surface can lock in membership. */
export const INTERNAL_FIXTURE_IDS = new Set<string>([
  // Diagnostic-only: asserts env-var plumbing through the Playwright
  // subprocess. No real IdP traffic, no customer value.
  'noopSmoke',
])

/** Map a SystemFixture `requiredFields` token to the credential step
 * property it corresponds to. Lookup table rather than a switch so the
 * test surface (`findMissingRequiredFields`) can verify each mapping
 * independently. LOGIN_URL is satisfied when a step has a `loginUrl`
 * captured (by the operator typing the Sign-in address OR the agent
 * during exploration). */
const REQUIRED_FIELD_TO_STEP_PROP: Record<
  SystemFixtureSummary['requiredFields'][number],
  keyof CredentialStep
> = {
  USERNAME: 'username',
  PASSWORD: 'password',
  TOKEN: 'token',
  TOTP_SECRET: 'totpSecret',
  LOGIN_URL: 'loginUrl',
}

/** Plain-language label for each credential-field token. Used in
 * customer-facing strings (hint sentence under the picker, missing-
 * field warnings). Keep these in customer voice — no env-var shapes, no
 * system vocabulary. The token itself stays in code paths; this map is
 * the only place a UI string sees it. */
const FIELD_LABEL: Record<SystemFixtureSummary['requiredFields'][number], string> = {
  USERNAME: 'username',
  PASSWORD: 'password',
  TOKEN: 'API token',
  TOTP_SECRET: 'authenticator code',
  LOGIN_URL: 'sign-in address',
}

/** Join a list of labels into prose: ['a','b','c'] → 'a, b, and c';
 * ['a','b'] → 'a and b'; ['a'] → 'a'. Customer-facing punctuation. */
function joinForProse(items: string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/** Return the SystemFixture's required fields that are empty on `step`.
 * Returns [] when no library fixture is bound or when the bound fixture
 * is not in the catalog (stale binding — defensive; never warn about a
 * fixture we can't validate against). Exported as a pure helper so the
 * test surface can lock the logic down without rendering. */
export function findMissingRequiredFields(
  binding: LoginFixtureBinding | undefined,
  systemFixtures: SystemFixtureSummary[],
  step: CredentialStep,
  /** #1884 follow-up — the sign-in helper that will ACTUALLY run for this
   * credential, resolved server-side. Set for an explicitly chosen helper
   * AND for one already installed for the role while the credential is on
   * `auto`. Falls back to the explicit binding when absent (older payload).
   * Without it, an `auto` credential whose curated helper is installed read
   * as needing nothing, so its sign-in address showed "optional" and the run
   * then failed on the missing address. */
  effectiveFixtureId?: string | null,
  /** #1913 — true when this role has a recorded sign-in path to replay. The
   * recording reaches the form on its own, so the sign-in address stops being
   * something the operator must supply, exactly as a discovery-capable fixture
   * already drops it. Mirrors the server's `requiredFieldsOperatorMustSupply`. */
  hasUsableRecording?: boolean,
): SystemFixtureSummary['requiredFields'] {
  const fixtureId =
    effectiveFixtureId ?? (binding?.mode === 'library' ? binding.libraryId : undefined)
  if (!fixtureId) return []
  const fixture = systemFixtures.find(f => f.id === fixtureId)
  if (!fixture) return []
  // A helper that can find its own sign-in address — or a role with a recorded
  // path to replay — does not need the operator to supply one. Mirrors
  // `requiredFieldsOperatorMustSupply` on the server so the form and the save
  // agree.
  const mustSupply = fixture.authEntryPathHints?.length || hasUsableRecording
    ? fixture.requiredFields.filter(f => f !== 'LOGIN_URL')
    : fixture.requiredFields
  return mustSupply.filter(field => {
    const prop = REQUIRED_FIELD_TO_STEP_PROP[field]
    const value = step[prop]
    return !value || (typeof value === 'string' && !value.trim())
  })
}

/** Plain-language warning that names the login details a bound sign-in
 * helper still needs (#886 / #1312). One component, two call sites — the
 * inline first-step "Login details" section (`location="here"`) and a
 * multi-step `StepCard` (`location="in Step N"`). */
function MissingFieldsWarning({ fields, helperName, location }: {
  fields: SystemFixtureSummary['requiredFields']
  helperName?: string
  location: string
}) {
  if (fields.length === 0) return null
  return (
    <p className="text-[11px] text-amber-500 flex items-baseline gap-1" role="alert">
      <span aria-hidden="true">⚠</span>
      <span>
        {`Add the ${joinForProse(fields.map(f => FIELD_LABEL[f]))} ${location} — ${helperName ?? 'this sign-in'} can't sign in without ${fields.length === 1 ? 'it' : 'them'}.`}
      </span>
    </p>
  )
}

/** Plain-language label for each custom-flow row in the credential auth
 * picker (#887). The dropdown subsumes the old Step-1 method dropdown,
 * so these labels are the only place a customer reads the auth flow's
 * name — they must pass the "would I say this in a demo" test (#886). */
const CUSTOM_FLOW_ROWS: ReadonlyArray<{ method: AuthMethod; label: string }> = [
  { method: 'basic',       label: 'My own login form (username + password)' },
  { method: 'mfa',         label: 'My own login form (username + password + authenticator code)' },
  { method: 'oauth',       label: 'My own login form (OAuth client credentials)' },
  { method: 'bearer',      label: 'My own login form (Bearer token / API key)' },
  { method: 'certificate', label: 'My own login form (Certificate)' },
]

/** Picker selection — the credential editor turns this into a paired
 * update of `step.method` (Step 1) and `loginFixtureBinding`. Kept as
 * a tagged record (not just a stringy dropdown value) so the editor
 * code reads as "this is what the user chose," not "decode the
 * string." */
export type CredentialAuthSelection = {
  method: AuthMethod
  binding: LoginFixtureBinding
}

/** Editable per-credential dropdown that subsumes the Step-1 method
 * choice (#887). Three logical groups, rendered as <optgroup> in the
 * native <select> so the section structure is screen-reader-friendly:
 *
 *   1. Custom flows  — one row per AuthMethod with plain-language
 *      labels. Maps to (method=<chosen>, binding={mode:'none'}).
 *   2. Library entries — one row per non-internal SystemFixture row.
 *      Maps to (method=<fixture's auth method>, binding={mode:'library',
 *      libraryId, libraryVersion}).
 *   3. Auto-detect — single row. Maps to (method=<keep current>,
 *      binding={mode:'auto'}).
 *
 * Migration mapping for existing credentials (silent, no banner):
 *   - method='X' + binding undefined or {mode:'none'} → custom:X
 *   - binding {mode:'library', libraryId:Y}           → library:Y
 *   - binding {mode:'auto'}                           → auto
 *
 * Exported so the test surface can exercise selection/version-stamping
 * behavior without standing up the whole Test Data page. */
export function CredentialAuthPicker({ credentialId, step1Method, binding, systemFixtures, onChange }: {
  credentialId: string
  step1Method: AuthMethod
  binding: LoginFixtureBinding | undefined
  systemFixtures: SystemFixtureSummary[]
  onChange: (selection: CredentialAuthSelection) => void
}) {
  const selectedValue: string = (() => {
    if (binding?.mode === 'library' && binding.libraryId) {
      if (systemFixtures.some(f => f.id === binding.libraryId)) {
        return `library:${binding.libraryId}`
      }
    }
    if (binding?.mode === 'auto') return 'auto'
    return `custom:${step1Method}`
  })()

  const selectedFixture = binding?.mode === 'library' && binding.libraryId
    ? systemFixtures.find(f => f.id === binding.libraryId)
    : undefined

  // Infer the AuthMethod a library fixture expects from its
  // requiredFields. step.method drives StepFieldGrid's rendered field
  // set, so the inference must produce a method whose field set is a
  // superset of the fixture's requirements.
  const fixtureMethod = (f: SystemFixtureSummary): AuthMethod => {
    if (f.requiredFields.includes('TOTP_SECRET')) return 'mfa'
    if (f.requiredFields.includes('TOKEN')) return 'bearer'
    return 'basic'
  }

  function handleChange(value: string) {
    if (value === 'auto') {
      onChange({ method: step1Method, binding: { mode: 'auto' } })
      return
    }
    if (value.startsWith('library:')) {
      const id = value.slice('library:'.length)
      const fixture = systemFixtures.find(f => f.id === id)
      if (!fixture) return
      onChange({
        method: fixtureMethod(fixture),
        binding: { mode: 'library', libraryId: fixture.id, libraryVersion: fixture.version },
      })
      return
    }
    if (value.startsWith('custom:')) {
      const method = value.slice('custom:'.length) as AuthMethod
      onChange({ method, binding: { mode: 'none' } })
      return
    }
  }

  const selectId = `credentialAuthPicker-${credentialId}`

  return (
    <div className="space-y-1.5">
      <Label htmlFor={selectId} className="text-xs">How OttoTester signs in</Label>
      <div className="relative">
        <select
          id={selectId}
          className="flex h-9 w-full appearance-none rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring pr-8"
          value={selectedValue}
          onChange={e => handleChange(e.target.value)}
        >
          <optgroup label="Your own login form">
            {CUSTOM_FLOW_ROWS.map(row => (
              <option key={`custom:${row.method}`} value={`custom:${row.method}`} className="bg-popover text-popover-foreground">
                {row.label}
              </option>
            ))}
          </optgroup>
          {/* Always render the middle optgroup so the dropdown's
           * three-section structure is stable across the catalog
           * fetch lifecycle (empty → disabled placeholder). */}
          <optgroup label="Pre-built sign-in flows">
            {systemFixtures.length === 0 ? (
              <option value="__no-library__" disabled className="bg-popover text-muted-foreground">
                No pre-built sign-in flows available
              </option>
            ) : (
              systemFixtures.map(f => (
                <option key={`library:${f.id}`} value={`library:${f.id}`} className="bg-popover text-popover-foreground">
                  {`${f.name}    v${f.version}`}
                </option>
              ))
            )}
          </optgroup>
          <optgroup label="Or">
            <option value="auto" className="bg-popover text-popover-foreground">
              Let OttoTester figure it out when it explores the app
            </option>
          </optgroup>
        </select>
        <ChevronDown className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50 pointer-events-none" />
      </div>
      {selectedFixture && selectedFixture.requiredFields.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {selectedFixture.name} needs the {joinForProse(selectedFixture.requiredFields.map(f => FIELD_LABEL[f]))} in the login details below.
        </p>
      )}
    </div>
  )
}

/** #1964 — leading or trailing whitespace in a bearer field (password,
 * bearer token, client secret) can be a real character, so we never trim it
 * silently. We flag it at entry instead and let the operator remove it.
 * Renders nothing unless the value actually has surrounding whitespace. */
function WhitespaceWarning({ value, fieldLabel, onTrim }: {
  value: string
  fieldLabel: string
  onTrim: () => void
}) {
  if (!value || value === value.trim()) return null
  const leadWs = value.slice(0, value.length - value.trimStart().length)
  const trailWs = value.slice(value.trimEnd().length)
  const surrounding = leadWs + trailWs
  const hasBreak = /[\n\r]/.test(surrounding)
  const hasSpace = /[^\n\r]/.test(surrounding)
  // Name only what is actually there — a value with a leading space AND a
  // trailing line break says "space or line break", not one or the other.
  const kind = hasBreak && hasSpace ? 'space or line break' : hasBreak ? 'line break' : 'space'
  const where = leadWs && trailWs ? 'starts and ends' : leadWs ? 'starts' : 'ends'
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-amber-600">
      <span>This {fieldLabel} {where} with a {kind} — remove it if you didn't mean to include it.</span>
      <button type="button" onClick={onTrim} className="underline font-medium hover:text-amber-700">
        Remove the {kind} from the {fieldLabel}
      </button>
    </div>
  )
}

/** The login-field grid for one step — switches the visible fields on the
 * step's auth method (#1312 extracted this from `StepCard` so the first
 * step can render it inline, with no "Step 1" card, while multi-step
 * credentials still render it inside a `StepCard`). Secret-bearing fields
 * use `PasswordInput` with `reMaskOnBlur` (#1206). */
export function StepFieldGrid({ step, onChange }: {
  step: CredentialStep
  onChange: (updates: Partial<CredentialStep>) => void
}) {
  // Every field carries a visible label above it (#1312) — placeholders
  // alone vanish once a field is filled, leaving an anonymous box.
  return (
    <div className="grid grid-cols-2 gap-2">
      {(step.method === 'basic' || step.method === 'mfa') && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Username or email</Label>
            <Input className="h-8 text-xs" value={step.username || ''} onChange={e => onChange({ username: e.target.value })} placeholder="e.g. jane@example.gov" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Password</Label>
            <PasswordInput className="h-8 text-xs" revealLabel="password" reMaskOnBlur value={step.password || ''} onChange={e => onChange({ password: e.target.value })} />
            <WhitespaceWarning value={step.password || ''} fieldLabel="password" onTrim={() => onChange({ password: (step.password || '').trim() })} />
          </div>
        </>
      )}
      {step.method === 'mfa' && (
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Authenticator code (TOTP secret)</Label>
          <PasswordInput className="h-8 text-xs" revealLabel="authenticator code" reMaskOnBlur value={step.totpSecret || ''} onChange={e => onChange({ totpSecret: e.target.value })} placeholder="Base32 secret" />
        </div>
      )}
      {step.method === 'bearer' && (
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Bearer token</Label>
          <PasswordInput className="h-8 text-xs" revealLabel="bearer token" reMaskOnBlur value={step.token || ''} onChange={e => onChange({ token: e.target.value })} placeholder="Token or API key" />
          <WhitespaceWarning value={step.token || ''} fieldLabel="bearer token" onTrim={() => onChange({ token: (step.token || '').trim() })} />
        </div>
      )}
      {step.method === 'oauth' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Client ID</Label>
            <Input className="h-8 text-xs" value={step.clientId || ''} onChange={e => onChange({ clientId: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Client secret</Label>
            <PasswordInput className="h-8 text-xs" revealLabel="client secret" reMaskOnBlur value={step.clientSecret || ''} onChange={e => onChange({ clientSecret: e.target.value })} />
            <WhitespaceWarning value={step.clientSecret || ''} fieldLabel="client secret" onTrim={() => onChange({ clientSecret: (step.clientSecret || '').trim() })} />
          </div>
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">Token endpoint</Label>
            <Input className="h-8 text-xs" value={step.tokenEndpoint || ''} onChange={e => onChange({ tokenEndpoint: e.target.value })} placeholder="https://…/oauth/token" />
          </div>
        </>
      )}
    </div>
  )
}

export function StepCard({ step, stepNumber, canRemove, showMethodDropdown = true, missingRequiredFields, boundFixtureName, onChange, onRemove }: {
  step: CredentialStep
  stepNumber: number
  canRemove: boolean
  /** Whether to render the per-step method dropdown. On the first step the
   * credential-level sign-in picker (#887) is the sole driver of method,
   * so callers pass false; on later steps (a multi-page sign-in) the
   * per-step dropdown is the right control. Defaults true for back-compat. */
  showMethodDropdown?: boolean
  /** Names from the bound helper's `requiredFields` that are empty on
   * this step. Pass `[]` when no helper is bound. */
  missingRequiredFields: SystemFixtureSummary['requiredFields']
  /** Customer-facing name of the bound helper, so the warning can name it
   * directly. Undefined when no helper is bound. */
  boundFixtureName?: string
  onChange: (updates: Partial<CredentialStep>) => void
  onRemove: () => void
}) {
  return (
    <div className="border rounded-md p-3 bg-background space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Step {stepNumber}</span>
        <div className="flex-1" />
        {showMethodDropdown && (
          <select
            className="h-8 text-xs bg-transparent border border-border rounded px-1"
            value={step.method}
            onChange={e => onChange({
              method: e.target.value as AuthMethod,
              username: undefined, password: undefined, token: undefined,
              totpSecret: undefined, clientId: undefined, clientSecret: undefined, tokenEndpoint: undefined,
            })}
          >
            {METHOD_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          title={canRemove ? 'Remove step' : 'A credential must have at least one step'}
          disabled={!canRemove}
          onClick={onRemove}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      <StepFieldGrid step={step} onChange={onChange} />

      <Input
        className="h-8 text-xs"
        value={step.notes || ''}
        placeholder="Notes — a hint about this sign-in step (e.g. 'federated sign-in step')"
        onChange={e => onChange({ notes: e.target.value })}
      />

      <MissingFieldsWarning fields={missingRequiredFields} helperName={boundFixtureName} location={`in Step ${stepNumber}`} />
    </div>
  )
}

function newStep(position: number): CredentialStep {
  return { name: `step-${position}`, method: 'basic' }
}

/** Keep step names positional so the manifest shown to the agent always
 * reads step-1, step-2, etc. in order. Users disambiguate steps via the
 * per-step `notes` field, not via the name. */
function renumberSteps(cred: Credential): Credential {
  return {
    ...cred,
    steps: cred.steps.map((s, i) => ({ ...s, name: `step-${i + 1}` })),
  }
}

function summarizeSteps(steps: CredentialStep[]): string {
  if (steps.length === 1) {
    const s = steps[0]
    const user = s.username ? ` · ${s.username}` : ''
    return `1 step · ${s.method}${user}`
  }
  const chain = steps.map(s => s.method).join(' → ')
  return `${steps.length} steps · ${chain}`
}

function makeUniqueRole(base: string, existing: Credential[]): string {
  const used = new Set(existing.map(c => (c.role || '').trim()).filter(Boolean))
  if (!used.has(base)) return base
  for (let n = 2; n < 100; n++) {
    const candidate = `${base}-${n}`
    if (!used.has(candidate)) return candidate
  }
  return `${base}-${crypto.randomUUID().slice(0, 6)}`
}

// ── Self-loading wrapper ──────────────────────────────────────────────
//
// Reads the selected variant from app context, loads its credentials, and
// persists on change. The old surface received these as props from the now-
// dead TestDataPage; the new /test-data "Credentials" tab mounts this
// directly.
export function VariantCredentialsPanel() {
  const { selectedVariantId, selectedVariant } = useAppContext()
  const toast = useToast()
  const [credentials, setCredentials] = useState<Credential[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedVariantId) { setCredentials([]); return }
    setLoadError(null)
    testDataAPI.get(selectedVariantId).then((doc: any) => {
      // Server normalizes credentials on read into the canonical steps[]
      // shape and attaches `detectedSignInMethod` (#1312). Defensive:
      // backfill id and ensure at least one step.
      const creds: Credential[] = (doc.credentials || []).map((c: any) => {
        const steps: CredentialStep[] = Array.isArray(c.steps) && c.steps.length > 0
          ? c.steps
          : [{ name: 'default', method: (c.method as AuthMethod) || 'basic' }]
        return {
          id: c.id || crypto.randomUUID(),
          role: c.role,
          label: c.label,
          steps,
          loginFixtureBinding: c.loginFixtureBinding,
          detectedSignInMethod: c.detectedSignInMethod ?? null,
        }
      })
      setCredentials(creds)
    }).catch((err: unknown) => {
      setLoadError(`Couldn't load credentials: ${err instanceof Error ? err.message : String(err)}`)
    })
  }, [selectedVariantId])

  function persist(creds: Credential[]) {
    setCredentials(creds)
    if (!selectedVariantId) return
    testDataAPI.setCredentials(selectedVariantId, creds).catch((err: unknown) => {
      console.error('[Credentials] save failed:', err)
      toast({
        title: 'Failed to save credentials',
        description: err instanceof Error ? err.message : String(err),
        variant: 'error',
      })
    })
  }

  if (!selectedVariantId) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        Select a variant to manage its credentials.
      </div>
    )
  }

  return (
    <div>
      {loadError && (
        <div className="m-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {loadError}
        </div>
      )}
      <CredentialsEditor
        credentials={credentials}
        onChange={persist}
        variantId={selectedVariantId}
        variantName={selectedVariant?.name}
        variantUrl={selectedVariant?.baseUrl}
      />
    </div>
  )
}
